import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useFollowShop } from "../../hooks/useFollowActions";
import useAuthentication from "../../hooks/useAuthentication";
import useGoToLogin from "../../hooks/useGoToLogin";
import Avatar from "../../common-components/Avatar";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import { resolveImageUri } from "../../utility/constants";
import { buildShopPath } from "../../utility/utilityFunctions";

const AVATAR_SIZE = 72;

const ShopCard = ({ shop, onFollowed }) => {
  const goToLogin = useGoToLogin();
  const { isLoggedIn } = useAuthentication();
  const { mutate: toggleFollow, isPending } = useFollowShop(shop.id, {
    onSuccess: onFollowed,
  });

  const avatarUri = shop.profilePhoto
    ? resolveImageUri(shop.profilePhoto)
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: 108,
        flexShrink: 0,
        mr: 3,
      }}
    >
      <Box
        component={Link}
        to={buildShopPath(shop)}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Avatar src={avatarUri} name={shop.shopName} size={AVATAR_SIZE} />
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            textAlign: "center",
            mt: 0.75,
            color: "#374151",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.3,
          }}
        >
          {shop.shopName}
          {shop.isIdentityVerified && (
            <VerifiedBadge
              size={11}
              sx={{ verticalAlign: "middle", ml: "3px" }}
            />
          )}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        disabled={isPending}
        onClick={() => {
          if (!isLoggedIn) {
            goToLogin();
            return;
          }
          toggleFollow(false);
        }}
        sx={{
          mt: 0.75,
          borderRadius: 5,
          fontSize: 11,
          minWidth: 64,
          py: 0.25,
          borderColor: "#046287",
          color: "#046287",
        }}
      >
        {isPending ? (
          <CircularProgress size={12} sx={{ color: "#046287" }} />
        ) : (
          "Follow"
        )}
      </Button>
    </Box>
  );
};

// Fixed snapshot passed down from the feed's recurrence cursor — this row
// never gets new props once rendered, so a followed shop is removed from
// view locally rather than relying on invalidation to refresh the row.
const FeedShopRecommendationsRow = ({
  shops,
  heading = "Shops you might like",
}) => {
  const [followedIds, setFollowedIds] = useState(() => new Set());

  const visibleShops = useMemo(
    () => (shops ?? []).filter((shop) => !followedIds.has(shop.id?.toString())),
    [shops, followedIds],
  );

  if (!visibleShops.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ mb: 1.5, color: "#111827" }}
      >
        {heading}
      </Typography>
      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          pb: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {visibleShops.map((shop) => (
          <ShopCard
            key={shop.id}
            shop={shop}
            onFollowed={() =>
              setFollowedIds((prev) => new Set(prev).add(shop.id?.toString()))
            }
          />
        ))}
      </Box>
    </Box>
  );
};

export default FeedShopRecommendationsRow;
